/**
 * Gestisce l'animazione degli elementi della timeline.
 * Aggiunge la classe `in-view` a ciascun elemento <li> della timeline
 * quando entra nel viewport, usando IntersectionObserver (con fallback
 * alla verifica via getBoundingClientRect per browser legacy).
 */
export class Timeline {
    private readonly items: NodeListOf<HTMLElement>;

    constructor(selector: string = '.timeline li') {
        this.items = document.querySelectorAll<HTMLElement>(selector);
    }

    public init(): void {
        if (this.items.length === 0) return;

        if ('IntersectionObserver' in window) {
            this.initWithObserver();
        } else {
            this.initWithScrollEvents();
        }
    }

    // ====== IntersectionObserver (approccio moderno) ======

    private initWithObserver(): void {
        const observer = new IntersectionObserver(
            (entries: IntersectionObserverEntry[]) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('in-view');
                        // Smette di osservare dopo la prima animazione
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0, rootMargin: '0px 0px -50px 0px' }
        );

        this.items.forEach(item => observer.observe(item));
    }

    // ====== Fallback scroll/resize (browser legacy) ======

    private initWithScrollEvents(): void {
        const check = () => this.checkVisibility();
        window.addEventListener('load', check);
        window.addEventListener('resize', check);
        window.addEventListener('scroll', check);
        check();
    }

    private checkVisibility(): void {
        this.items.forEach(item => {
            if (this.isInViewport(item)) {
                item.classList.add('in-view');
            }
        });
    }

    private isInViewport(el: HTMLElement): boolean {
        const rect = el.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const windowWidth  = window.innerWidth  || document.documentElement.clientWidth;
        // Visibilità parziale: basta che l'elemento intersechi il viewport
        return (
            rect.bottom > 0 &&
            rect.right  > 0 &&
            rect.top    < windowHeight &&
            rect.left   < windowWidth
        );
    }
}
